import { Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { classValidatorResolver } from '@hookform/resolvers/class-validator';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { useCreateDomain, useGetDomain, useUpdateDomain } from '../../hooks/api/use-domains.hook';
import { DomainApiModel } from '../../../../types/models/domain.model';
import { LoadingIndicator } from '../LoadingIndicator';
import { DomainForm } from './DomainForm';
import { getDirtyValues } from '../../utils/react-hook-form';

export const AddEditDomain = () => {
  const navigate = useNavigate();
  const { domainId } = useParams<{ domainId: string }>();

  const {
    domain,
    loading: loadingDomain,
    error: gettingError,
  } = useGetDomain<DomainApiModel>({ domainId: Number(domainId) });
  const [createDomain, { loading: creatingDomain, error: createError }] = useCreateDomain();
  const [updateDomain, { loading: updatingDomain, error: updateError }] = useUpdateDomain();

  const {
    register,
    handleSubmit,
    formState: { errors, dirtyFields, isSubmitting },
  } = useForm<DomainForm>({ resolver: classValidatorResolver(DomainForm), values: domain });

  const onSubmit: SubmitHandler<DomainForm> = async (submittedValues) => {
    const updatedValues = getDirtyValues(dirtyFields, submittedValues);
    await (domain ? updateDomain({ ...updatedValues, domainId: domain.id }) : createDomain(submittedValues));
    navigate('..');
  };

  const renderContent = () => {
    if (gettingError) {
      return <Alert severity="error">{gettingError?.response?.data.message}</Alert>;
    }

    return (
      <>
        <TextField
          {...register('name')}
          variant="outlined"
          sx={{ mt: '1rem' }}
          disabled={creatingDomain || updatingDomain}
          error={Boolean(errors.name)}
          helperText={errors.name?.message}
          label="Name"
          margin="dense"
          size="small"
          fullWidth
        />
        <TextField
          {...register('shortName')}
          variant="outlined"
          sx={{ mt: '1rem' }}
          disabled={creatingDomain || updatingDomain}
          error={Boolean(errors.shortName)}
          helperText={errors.shortName?.message}
          label="Short name"
          margin="dense"
          size="small"
          fullWidth
        />
        <TextField
          {...register('description')}
          variant="outlined"
          sx={{ mt: '1rem' }}
          disabled={creatingDomain || updatingDomain}
          error={Boolean(errors.description)}
          helperText={errors.description?.message}
          label="Description"
          margin="dense"
          size="small"
          fullWidth
        />
      </>
    );
  };

  if (loadingDomain) {
    // FIXME: workaround for react-use-form not resetting controlled fields in a dialog
    return <LoadingIndicator />;
  }

  return (
    <Dialog open={true}>
      {loadingDomain ? (
        <LoadingIndicator />
      ) : (
        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogTitle>{`${domain ? 'Edit' : 'Add'} Domain`}</DialogTitle>
          <DialogContent>
            {renderContent()}
            {createError || updateError ? (
              <Alert sx={{ mt: '1rem' }} severity="error">
                {createError?.response?.data?.message || updateError?.response?.data?.message}
              </Alert>
            ) : null}
          </DialogContent>
          <DialogActions>
            <Button disabled={isSubmitting} variant="outlined" onClick={() => navigate('..')} color="primary">
              Cancel
            </Button>
            <LoadingButton
              color="primary"
              type="submit"
              variant="contained"
              loading={isSubmitting || Boolean(gettingError)}
            >
              {domain ? 'Save' : 'Create'}
            </LoadingButton>
          </DialogActions>
        </form>
      )}
    </Dialog>
  );
};
